import React, { useState, useEffect, useRef } from 'react';
import { Package, Layers, Scale, DollarSign } from 'lucide-react';
import Modal from '../../ui/Modal';
import PricingByQuantity from '../../pricing/PricingByQuantity';
import { formatMoney } from '../../../utils/formatters';
import './QuantityModal.css';

const esUnidadPeso = (unidad) => {
    if (!unidad) return false;
    const u = unidad.toLowerCase().trim();
    return ['kilo', 'kilos', 'kg', 'kilogramo', 'kilogramos', 'gramo', 'gramos', 'g', 'gr'].includes(u);
};

/**
 * Modal para ingresar cantidad de un producto
 * Incluye selector de presentaciones, cálculo de precios, báscula y modo monto
 */
const QuantityModal = ({
    isOpen,
    onClose,
    onConfirm,
    producto,
    initialQuantity = 1,
    equivalencias = [],
    loadingEquivalencias = false,
    getCustomerPriceFromPresentacion,
    cliente_id = null
}) => {
    const [cantidad, setCantidad] = useState(initialQuantity);
    const [debouncedQuantity, setDebouncedQuantity] = useState(initialQuantity);
    const [calculatedPricing, setCalculatedPricing] = useState(null);
    const [presentacionSeleccionada, setPresentacionSeleccionada] = useState(null);
    const [readingScale, setReadingScale] = useState(false);
    const [modoMonto, setModoMonto] = useState(false);
    const [monto, setMonto] = useState('');

    const quantityInputRef = useRef(null);
    const quantityDebounceTimer = useRef(null);
    const justOpened = useRef(false);

    const getPresentacionMayorPrecio = (eqs) => {
        if (!eqs || eqs.length === 0) return null;
        return eqs.reduce((mayor, eq) => {
            const precioActual = parseFloat(eq.precio_lista) || 0;
            const precioMayor = parseFloat(mayor.precio_lista) || 0;
            return precioActual > precioMayor ? eq : mayor;
        }, eqs[0]);
    };

    // Resetear estado cuando cambia el producto
    useEffect(() => {
        if (isOpen && producto) {
            const esPeso = esUnidadPeso(producto?.unidad_medida);
            setCantidad(esPeso ? '' : initialQuantity);
            setDebouncedQuantity(esPeso ? 0 : initialQuantity);
            setCalculatedPricing(null);
            setPresentacionSeleccionada(getPresentacionMayorPrecio(equivalencias));
            setModoMonto(false);
            setMonto('');
            justOpened.current = true;

            setTimeout(() => {
                if (quantityInputRef.current) {
                    quantityInputRef.current.focus();
                    if (justOpened.current) {
                        quantityInputRef.current.select();
                        justOpened.current = false;
                    }
                }
            }, 100);
        }
    }, [isOpen, producto?.id, initialQuantity]);

    // Actualizar presentacion seleccionada cuando llegan equivalencias
    useEffect(() => {
        if (equivalencias.length > 0 && !presentacionSeleccionada) {
            setPresentacionSeleccionada(getPresentacionMayorPrecio(equivalencias));
        }
    }, [equivalencias]);

    // Debounce para cantidad
    useEffect(() => {
        if (quantityDebounceTimer.current) {
            clearTimeout(quantityDebounceTimer.current);
        }

        quantityDebounceTimer.current = setTimeout(() => {
            const cantidadNum = parseFloat(cantidad);
            if (!isNaN(cantidadNum) && cantidadNum > 0) {
                setDebouncedQuantity(cantidadNum);
            }
        }, 30);

        return () => {
            if (quantityDebounceTimer.current) {
                clearTimeout(quantityDebounceTimer.current);
            }
        };
    }, [cantidad]);

    // Polling automático de báscula mientras el modal está abierto para producto de peso
    useEffect(() => {
        if (!isOpen || !producto) return;
        if (!esUnidadPeso(producto?.unidad_medida)) return;
        if (!window.electron?.ipcRenderer) return;
        if (modoMonto) return;

        const intervalId = setInterval(async () => {
            try {
                const status = await window.electron.ipcRenderer.invoke('scale:status');
                if (!status.connected) return;
                const result = await window.electron.ipcRenderer.invoke('scale:read-weight');
                if (result.success && !result.overload && result.weight != null && result.weight > 0) {
                    setCantidad(result.weight);
                }
            } catch (e) {
                // Silencioso
            }
        }, 500);

        return () => clearInterval(intervalId);
    }, [isOpen, producto?.id, modoMonto]);

    const handlePriceChange = (pricingInfo) => {
        setCalculatedPricing(pricingInfo);
    };

    const handleConfirm = () => {
        const cantidadNum = parseFloat(cantidad);
        if (isNaN(cantidadNum) || cantidadNum <= 0) {
            return;
        }

        onConfirm({
            cantidad: cantidadNum,
            calculatedPricing,
            presentacionSeleccionada,
            equivalencias
        });
    };

    const getPrecioUnitario = () => {
        const productoActivo = presentacionSeleccionada || producto;
        if (getCustomerPriceFromPresentacion) {
            return getCustomerPriceFromPresentacion(productoActivo);
        }
        return parseFloat(productoActivo?.precio_lista) || 0;
    };

    const toggleModoMonto = () => {
        const nuevoModo = !modoMonto;
        setModoMonto(nuevoModo);
        setMonto('');
        setCantidad(nuevoModo ? '' : '');
        setTimeout(() => quantityInputRef.current?.focus(), 100);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleConfirm();
        }

        if ((e.key === 'b' || e.key === 'B') && esUnidadPeso(producto?.unidad_medida)) {
            e.preventDefault();
            handleReadScale();
            return;
        }

        if ((e.key === 'c' || e.key === 'C') && esUnidadPeso(producto?.unidad_medida)) {
            e.preventDefault();
            toggleModoMonto();
            return;
        }

        // Flechas izquierda/derecha para cambiar presentación
        if (equivalencias.length > 1) {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const currentIndex = equivalencias.findIndex(eq => eq.producto_id === presentacionSeleccionada?.producto_id);
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : equivalencias.length - 1;
                setPresentacionSeleccionada(equivalencias[prevIndex]);
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                const currentIndex = equivalencias.findIndex(eq => eq.producto_id === presentacionSeleccionada?.producto_id);
                const nextIndex = currentIndex < equivalencias.length - 1 ? currentIndex + 1 : 0;
                setPresentacionSeleccionada(equivalencias[nextIndex]);
            }
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        if (modoMonto) {
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setMonto(value);
                const precioUnitario = getPrecioUnitario();
                const cantidadCalculada = precioUnitario > 0 ? Math.round((parseFloat(value) || 0) / precioUnitario * 1000) / 1000 : 0;
                setCantidad(cantidadCalculada > 0 ? cantidadCalculada : '');
            }
        } else {
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setCantidad(value);
            }
        }
    };

    const handleReadScale = async () => {
        if (!window.electron?.ipcRenderer || readingScale) return;
        setReadingScale(true);
        try {
            const status = await window.electron.ipcRenderer.invoke('scale:status');
            if (!status.connected) {
                return;
            }
            const result = await window.electron.ipcRenderer.invoke('scale:read-weight');
            if (result.success && !result.overload && result.weight != null) {
                setCantidad(result.weight);
            }
        } catch (e) {
            console.error('Error leyendo báscula:', e);
        } finally {
            setReadingScale(false);
            setTimeout(() => quantityInputRef.current?.focus(), 50);
        }
    };

    if (!isOpen || !producto) return null;

    const esPeso = esUnidadPeso(producto?.unidad_medida);
    const productoActivo = presentacionSeleccionada || producto;
    const precioMostrar = calculatedPricing?.precio_unitario ||
        (getCustomerPriceFromPresentacion ? getCustomerPriceFromPresentacion(productoActivo) : productoActivo.precio_lista);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Agregar al carrito"
            size="medium"
        >
            <div className="quantity-modal-content">
                {/* Producto info */}
                <div className="quantity-modal-product">
                    <Package size={28} />
                    <div className="product-details">
                        <span className="product-name">{producto.nombre}</span>
                        {producto.codigo_barras && (
                            <span className="product-code">{producto.codigo_barras}</span>
                        )}
                    </div>
                </div>

                {/* Selector de presentaciones */}
                {equivalencias.length > 1 && (
                    <div className="quantity-modal-presentaciones">
                        <label className="presentaciones-label">
                            <Layers size={18} />
                            Presentación:
                        </label>
                        <div className="presentaciones-list">
                            {equivalencias.map((eq) => (
                                <button
                                    key={eq.producto_id}
                                    className={`presentacion-btn ${presentacionSeleccionada?.producto_id === eq.producto_id ? 'active' : ''}`}
                                    onClick={() => setPresentacionSeleccionada(eq)}
                                    type="button"
                                >
                                    <span className="presentacion-unidad">{eq.unidad_medida}</span>
                                    <span className="presentacion-precio">{formatMoney(eq.precio_lista)}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {loadingEquivalencias && (
                    <div className="quantity-modal-loading">
                        Cargando presentaciones...
                    </div>
                )}

                {/* Input de cantidad */}
                <div className="quantity-modal-input-group">
                    <label htmlFor="quantity-input">{modoMonto ? 'Monto ($):' : 'Cantidad:'}</label>
                    <input
                        id="quantity-input"
                        ref={quantityInputRef}
                        type="text"
                        placeholder={modoMonto ? '' : (esPeso ? '' : 'Cantidad')}
                        value={modoMonto ? monto : cantidad}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onBlur={() => {
                            setTimeout(() => quantityInputRef.current?.focus(), 10);
                        }}
                        className="quantity-input"
                        autoComplete="off"
                    />
                    <span className="quantity-unit">{productoActivo.unidad_medida || 'pz'}</span>
                    {esPeso && (
                        <>
                            <button
                                type="button"
                                onClick={toggleModoMonto}
                                className="monto-btn"
                                title={modoMonto ? 'Cambiar a cantidad (C)' : 'Capturar por monto $ (C)'}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '8px 14px', marginLeft: '4px',
                                    border: '1px solid #d1d5db', borderRadius: '8px',
                                    background: modoMonto ? '#3b82f6' : '#f9fafb',
                                    color: modoMonto ? '#fff' : '#374151',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                <DollarSign size={20} />
                            </button>
                            <button
                                type="button"
                                onClick={handleReadScale}
                                disabled={readingScale}
                                className="scale-btn"
                                title="Leer báscula (B)"
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    padding: '8px 14px', marginLeft: '4px',
                                    border: '1px solid #d1d5db', borderRadius: '8px',
                                    background: readingScale ? '#9ca3af' : '#f9fafb',
                                    color: readingScale ? '#fff' : '#374151',
                                    cursor: readingScale ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                <Scale size={20} />
                            </button>
                        </>
                    )}
                </div>

                {/* Info de monto mode */}
                {modoMonto && parseFloat(cantidad) > 0 && (
                    <div className="quantity-modal-monto-info">
                        <span>= <strong>{cantidad}</strong> {productoActivo.unidad_medida || 'kg'}</span>
                        <span style={{ fontSize: '13px', color: '#047857' }}>
                            Precio: {formatMoney(getPrecioUnitario())}/{productoActivo.unidad_medida || 'kg'}
                        </span>
                    </div>
                )}

                {/* Componente de precios por cantidad */}
                <PricingByQuantity
                    productId={productoActivo.producto_id || productoActivo.id}
                    producto={productoActivo}
                    cantidad={debouncedQuantity}
                    cliente_id={cliente_id}
                    onPriceChange={handlePriceChange}
                    showDetails={true}
                    showRangeBreakdown={false}
                />

                {/* Total estimado */}
                <div className="quantity-modal-total">
                    <span>Total estimado:</span>
                    <span className="total-value">
                        {formatMoney(precioMostrar * (parseFloat(cantidad) || 0))}
                    </span>
                </div>

                {/* Ayuda de atajos */}
                <div className="quantity-modal-shortcuts">
                    <kbd>Enter</kbd> Agregar
                    {equivalencias.length > 1 && (
                        <>
                            <span className="shortcut-separator">|</span>
                            <kbd>← →</kbd> Cambiar unidad
                        </>
                    )}
                    {esPeso && (
                        <>
                            <span className="shortcut-separator">|</span>
                            <kbd>B</kbd> Báscula
                            <span className="shortcut-separator">|</span>
                            <kbd>C</kbd> Monto $
                        </>
                    )}
                    <span className="shortcut-separator">|</span>
                    <kbd>Esc</kbd> Cancelar
                </div>

                {/* Botones */}
                <div className="quantity-modal-actions">
                    <button
                        type="button"
                        className="btn-cancel"
                        onClick={onClose}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn-confirm"
                        onClick={handleConfirm}
                    >
                        Agregar
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default QuantityModal;
